import React, { useEffect, useState } from "react";
import { Storage, DataStore } from 'aws-amplify';
import { UserGroup } from "../../models";
import { StyleSheet, Text, TouchableOpacity, View, SafeAreaView, TextInput, Platform, Image } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as CmnUtil from '../components/CmnUtil';
import { SaveDialog } from "../components/CmnDiaLog";
import { appAlert, appMsg, fillZero, getCurrentUser } from "../components/CmnUtil";
import { Header } from "../components/Header";
import IconImages from "../components/IconImages";

/** ================================
*  組織情報変更画面
*  ================================*/
const GroupInfoUpdate = ({ navigation }: { navigation: any; }) => {

  // 組織情報
  const [userGroup, setUserGroup] = useState<UserGroup | undefined>(undefined);

  // 組織名入力
  const [groupName, setGroupName] = useState<string>("");

  // アイコン
  const [icon, setIcon] = useState<string | undefined>(undefined);

  // 保存ダイアログ表示
  const [saveDialogFlag, setSaveDialogFlag] = useState<boolean>(false);

  // 組織情報変更処理
  const updateUserGroup = async (iconKey: string) => {
    let msgText: string[] = ["", ""];
    await DataStore.save(
      UserGroup.copyOf(userGroup!, updated => {
        updated.groupName = groupName;
        updated.icon = icon ? iconKey : updated.icon;
      })
    )
      .then(() => msgText = ["success", "情報を変更しました。"])
      .catch(() => msgText = ["error", "変更に失敗しました。"]);

    return msgText;
  };

  // S3にアイコンを保存する処理
  const pathToImageFile = async (iconKey: string) => {
    let msgText = ["", ""];
    try {
      const response = await fetch(icon!);
      const blob = await response.blob();
      await Storage.put(iconKey, blob, {
        contentType: "image/jpeg",
      })
        .then(() => msgText = ["success", "S3に新しく画像を保存しました。"])
        .catch(() => msgText = ["error", "S3に画像を保存できませんでした。"]);
    } catch (err) {
      msgText = ["error", "保存に失敗しました。"];
    };
    return msgText;
  };

  // S3からアイコンのキーを削除する処理
  const deleteIconKey = async (iconKey: string) => {
    let msgText: string[] = ["", ""];

    await Storage.remove(iconKey)
      .then(() => msgText = ["success", "S3の古いアイコンを削除しました。"])
      .catch(() => msgText = ["error", "S3の古いアイコンの削除に失敗しました。"]);

    return msgText;
  };

  // S3のアイコンキーを作成
  const createIconKey = (): string => {
    const now = new Date();
    let key: string = "group_icon/icon_" + userGroup?.id + "_";
    key += fillZero(now.getFullYear(), 4);
    key += fillZero(now.getMonth() + 1, 2);
    key += fillZero(now.getDate(), 2);
    key += fillZero(now.getHours(), 2);
    key += fillZero(now.getMinutes(), 2);
    key += fillZero(now.getSeconds(), 2);
    return key;
  };

  const init = async () => {
    const appUser = await getCurrentUser();
    setUserGroup(appUser!.group);
    setGroupName(appUser!.group.groupName);
  }

  // 画面起動時に初期化
  useEffect(() => {
    init();
    (async () => {
      if (Platform.OS !== "web") {
        // 写真にアクセスする許可を求める
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== "granted") {
          // アクセスできなければアラート
          alert("Sorry, we need camera roll permissions to make this work!");
        };
      };
    })();
  }, []);

  // アイコン変更ボタンクリック
  const pickImage = async () => {
    // 携帯電話のライブラリから画像を選択するシステムUIを表示
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowEditiong: true,
      aspect: [4, 3],
      quality: 1,
    });

    if (!result.cancelled) {
      setIcon(result.uri);
    };
  };

  // 保存ダイアログのボタンを押したときの処理
  const onClickDialogButton = async () => {
    setSaveDialogFlag(false);

    if (groupName === "") {
      await appAlert("組織名は必須入力です。");
      return;
    }

    // S3に新しいキーを保存
    const iconKey = createIconKey();
    if (icon) {
      const ret = await pathToImageFile(iconKey);
      if (ret[0] == "error") {
        await appAlert(ret[1]);
        return;
      }
    }

    // AppUserに新しいキーを保存
    const ret = await updateUserGroup(iconKey);
    if (ret[0] == "error") {
      await appAlert(ret[1]);
      return;
    }

    // S3から古いキーを削除
    if (icon && userGroup?.icon) {
      const ret = await deleteIconKey(userGroup?.icon);
      if (ret[0] == "error") {
        await appAlert(ret[1]);
        return;
      }
    }

    await init();
    await appMsg("組織情報を変更しました。");
  };

  return (
    <SafeAreaView style={styles.container}>
      <SaveDialog
        cancelButtonName={"キャンセル"}
        saveButtonName={"保存"}
        canceAction={() => setSaveDialogFlag(false)}
        saveAction={() => onClickDialogButton()}
        message={"変更を保存しますか？"}
        visible={saveDialogFlag}
      />

      <Header onPress={() => navigation.goBack()} goBackText="＜" headerText="組織情報変更" />

      <View style={styles.formBlock}>
        <Text style={styles.formText}>組織名</Text>
        <TextInput style={styles.formInputName} value={groupName} onChangeText={setGroupName} />
      </View>
      <View style={styles.formBlock}>
        <Text style={styles.formText}>アイコン</Text>
        <View style={styles.iconWrap}>
          {icon ?
            <Image resizeMode="contain" source={{ uri: icon }} style={styles.groupIcon} defaultSource={IconImages.icGroup} />
            :
            <CmnUtil.S3Image imageKey={userGroup?.icon} style={styles.groupIcon} defaultSource={IconImages.icGroup} />
          }
          <TouchableOpacity style={{ marginLeft: 20 }} onPress={pickImage}>
            <Text style={{ fontSize: 18, textDecorationLine: "underline" }}>アイコンを変更する</Text>
          </TouchableOpacity>
        </View>
      </View>
      <TouchableOpacity style={styles.button} onPress={() => setSaveDialogFlag(true)}>
        <Text style={styles.buttonText}>保存</Text>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#E2F0D9",
    position: "relative",
  },
  button: {
    backgroundColor: "#7AAD47",
    borderWidth: 1,
    borderColor: "#057E32",
    borderStyle: "solid",
    width: 120,
    marginLeft: "auto",
    marginRight: "auto",
    height: 40,
    justifyContent: 'center',
    shadowColor: "#808080",
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 10,
  },
  buttonText: {
    color: "#ffffff",
    textAlign: "center",
    fontSize: 18,
  },
  formBlock: {
    marginBottom: 15,
  },
  formInputName: {
    backgroundColor: "#FFFFFF",
    height: 40,
    fontSize: 18,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "#417B9C",
    paddingHorizontal: 25,
  },
  formText: {
    fontSize: 18,
    marginBottom: 5,
    marginLeft: 20,
  },
  groupIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginLeft: 20,
    backgroundColor: "#808080",
  },
  iconWrap: {
    flexDirection: "row",
    alignItems: "flex-end",
  },
});

export default GroupInfoUpdate;